/**
 * Pre-defined metric descriptions for consistency across the dashboard.
 * These explain what each metric means in plain language.
 */
export const METRIC_DESCRIPTIONS = {
  // Chart titles
  queryVolume:
    'Shows the number of queries processed over time, split by successful queries and errors. Helps identify usage patterns and error spikes.',
  latencyTrends:
    'Tracks response times over time: vector search speed, time to first token (perceived thinking time), and total LLM generation time.',
  errorDistribution:
    'Breaks down errors by type to help identify and prioritize issues. Shows which error categories are most common.',
  tokenUsage:
    'Tracks LLM token consumption over time. Input tokens are your query plus retrieved documents; output tokens are the AI response. Essential for cost monitoring.',
  postFilterRejections:
    'Shows documents rejected by the defense-in-depth client filter over time. Should be near zero in a healthy system.',
  ingestionBySource:
    'Shows document ingestion activity broken down by data source (Fathom, HelpScout). Helps monitor data pipeline health.',
  syncOutcomes:
    'Shows the results of sync operations by source: items successfully synced, skipped (already exist), or failed.',

  // Latency metrics (header values)
  avgRetrieval:
    'Average time to search the knowledge base and retrieve relevant documents.',
  avgTtft:
    'Average Time to First Token - how long until the AI starts responding. This is what users perceive as "thinking time".',
  avgLlm:
    'Average time for the AI model to generate a complete response. This is typically the longest part of each query.',

  // Query metrics
  totalQueries: 'Total number of queries processed in this time period.',
  totalErrors: 'Number of queries that failed due to errors.',
  errorRate: 'Percentage of queries that resulted in errors.',

  // Token metrics
  totalTokens:
    'Total tokens (input + output) consumed. Tokens are the units used to measure AI model usage and cost.',
  avgTokensPerQuery:
    'Average tokens used per query. Useful for estimating costs and optimizing prompts.',
  inputTokens: 'Tokens sent to the AI model (your query + context documents).',
  outputTokens: 'Tokens generated by the AI model (the response).',

  // Retrieval quality metrics
  avgDocsRetrieved:
    'Average number of documents found per query. More documents provide richer context but increase processing time.',
  postFilterRejectionRate:
    'Percentage of documents rejected by the defense-in-depth client filter. Should be ~0% in a healthy system. Non-zero values indicate Bedrock metadata filtering may not be working correctly.',
  totalRejections:
    'Total number of documents rejected by the post-filter in this time period. Any rejections indicate a potential issue with Bedrock metadata filtering.',
  classificationMatchRate:
    'Percentage of documents that were successfully classified without needing manual review. Higher is better.',

  // Classification metrics (cards)
  classificationsCompleted:
    'Total documents processed by the tiered classification system in this time period.',
  classificationLatency:
    'Average time to classify a document through all tiers. Includes contact, name, keyword, and semantic matching.',
  pendingReview:
    'Documents that could not be confidently classified and are waiting for manual review.',
  classificationErrors:
    'Documents that failed to be classified due to errors. Check logs for details.',

  // Classification by tier
  classificationsByTier:
    'Breakdown of classifications by which tier resolved them. Contact and name matches are fastest; semantic matches use LLM analysis.',

  // Ingestion metrics (cards)
  docsIngested:
    'Documents successfully added to the knowledge base in this time period.',
  webhooks:
    'Real-time webhook events received from integrated services (Fathom, HelpScout).',
  syncJobs:
    'Scheduled batch synchronization jobs that completed. These run periodically to catch any missed webhooks.',
  ingestionErrors:
    'Documents that failed to be processed. Check logs for details on specific failures.',

  // Sync worker metrics (cards)
  itemsSynced:
    'New items successfully added to the knowledge base from sync jobs.',
  itemsSkipped:
    'Items that already exist in the knowledge base and were skipped during sync.',
  itemsFailed:
    'Items that failed to sync. May indicate API issues or data format problems.',
  avgProcessingRate:
    'Average speed of sync operations in items per second. Higher is better.',
  totalDuration:
    'Total time spent running sync jobs. Long durations may indicate performance issues.',
  apiCalls:
    'Number of external API requests made to data sources during sync operations.',
  s3Saves:
    'Number of documents saved to S3 storage. Each save represents a document added to the knowledge base.',

  // Latency percentiles
  p50: 'Median latency - 50% of requests are faster than this.',
  p90: '90th percentile - 90% of requests are faster than this.',
  p95: '95th percentile - used for SLA monitoring.',
  p99: '99th percentile - captures worst-case performance.',
} as const;

export type MetricKey = keyof typeof METRIC_DESCRIPTIONS;
